import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import useClickOutside from 'hooks/useClickOutside'
import zIndex from 'styles/tools/z-index'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import FlightSingleFilterStops from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterStops'
import FlightSingleFilterBags from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterBags'
import FlightSingleFilterAirlines from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterAirlines'
import FlightSingleFilterDuration from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterDuration'
import FlightSingleFilterPrice from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterPrice'
import FlightSingleFilterTime from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterTime'
import useFlightFilterLabels from 'hooks/Flights/useFlightFilterLabels'
import { FlightFilterType } from 'constants/flight'
import type { FlightsFiltersValues } from 'lib/flights/flightFilterTypes'
import type { UseTripFlightFiltersResult } from './useTripFlightFilters'

interface Props {
  filterControls: UseTripFlightFiltersResult
  originAirportCode: string
  destinationAirportCode: string
}

const ChipsRow = styled(Group)`
  flex-wrap: wrap;
`

const ChipWrapper = styled.div`
  position: relative;
`

// When a filter is active, keep the outline variant but give it a thicker,
// higher-contrast border so the selected state reads clearly.
const StyledChip = styled(AssistChip)`
  &.is-active {
    &&& {
      --chip-stroke-width: 2px;
      --chip-stroke-color: ${({ theme }) => theme.palette.neutral.surface.normalContrast};
    }
  }
`

// The dropdown is a plain absolutely-positioned child of ChipWrapper rather
// than a floating/portaled LuxKit DropdownList. Being a real DOM child means it
// follows the chip natively through scroll, window resize, and the flight
// drawer's slide-in transform — none of which floating-ui tracked reliably from
// inside the transformed drawer (it drifted to the middle / lagged on resize).
const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + ${rem(8)});
  left: 0;
  z-index: ${zIndex.menu};
  width: max-content;
  min-width: ${rem(240)};
  max-width: min(${rem(360)}, calc(100dvw - ${rem(32)}));
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.flat.small};
  border-radius: ${props => props.theme.borderRadius.S};
`

interface FilterChipDropdownProps {
  label: string
  isActive: boolean
  count?: number
  children: React.ReactNode
}

/** A single filter rendered as a chip that toggles its own anchored dropdown. */
function FilterChipDropdown({ label, isActive, count, children }: FilterChipDropdownProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const chipLabel = useMemo(() => count ? `${label} · ${count}` : label, [count, label])
  const endIcon = useMemo(() => <LineAngleDownIcon />, [])
  const handleToggleOpen = useCallback(() => setOpen(prev => !prev), [])
  const handleClose = useCallback(() => setOpen(false), [])

  // Close on outside click (the panel lives inside `ref`, so clicks within it
  // and on the chip itself are treated as inside) and on Escape.
  useClickOutside(ref, handleClose, open)
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, handleClose])

  return (
    <ChipWrapper ref={ref}>
      <StyledChip
        className={clsx({ 'is-active': isActive })}
        variant="outline"
        size="medium"
        endIcon={endIcon}
        onClick={handleToggleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {chipLabel}
      </StyledChip>

      {open && <DropdownPanel role="dialog" aria-label={label}>{children}</DropdownPanel>}
    </ChipWrapper>
  )
}

/**
 * The "Core 6" flight filters rendered as a row of chips, each opening its own
 * dropdown. Driven entirely by {@link useTripFlightFilters}; reuses the same
 * `FlightSingleFilter*` controls as the main flight search.
 */
function TripFlightFiltersPanel({ filterControls, originAirportCode, destinationAirportCode }: Props) {
  const intl = useIntl()
  const { flightFiltersModalLabelMap } = useFlightFilterLabels()
  const {
    filters,
    filterItems,
    activeFilters,
    onFilterChange,
    onPriceTypeChange,
    priceType,
    activeFilterCount,
    resetAllFilters,
  } = filterControls

  // The slider/checkbox controls emit native change events whose `name` is the
  // raw input name; map those to the corresponding filter field, mirroring the
  // standalone search panel.
  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let name = e.target.name.replace('::number', '')
    const value = e.target.value

    if (name === 'price.start') name = 'minPrices'
    if (name === 'price.end') name = 'maxPrices'
    if (name === 'minDepartureTime.start') name = 'minDepartureTime'
    if (name === 'minDepartureTime.end') name = 'maxDepartureTime'
    if (name === 'minArrivalTime.start') name = 'minArrivalTime'
    if (name === 'minArrivalTime.end') name = 'maxArrivalTime'

    onFilterChange(name as keyof FlightsFiltersValues, value)
  }, [onFilterChange])

  const isTimeActive = !!(filters.minDepartureTime || filters.maxDepartureTime || filters.minArrivalTime || filters.maxArrivalTime)
  const isPriceActive = !!(filters.minPrices || filters.maxPrices)

  return (
    <ChipsRow direction="horizontal" gap={8} verticalAlign="center">
      {filterItems[FlightFilterType.STOPS].length > 2 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.STOPS]}
          isActive={filters.stops.length > 0}
          count={filters.stops.length}
        >
          <FlightSingleFilterStops
            items={filterItems[FlightFilterType.STOPS]}
            onFilterChange={handleFilterChange}
            selectedItems={activeFilters[FlightFilterType.STOPS]}
          />
        </FilterChipDropdown>
      )}

      {filterItems[FlightFilterType.BAGS].length > 0 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.BAGS]}
          isActive={filters.bags.length > 0}
          count={filters.bags.length}
        >
          <FlightSingleFilterBags
            items={filterItems[FlightFilterType.BAGS]}
            onFilterChange={handleFilterChange}
            selectedItems={activeFilters[FlightFilterType.BAGS]}
          />
        </FilterChipDropdown>
      )}

      {filterItems[FlightFilterType.AIRLINES].length > 2 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.AIRLINES]}
          isActive={filters.airlines.length > 0}
          count={filters.airlines.length}
        >
          <FlightSingleFilterAirlines
            items={filterItems[FlightFilterType.AIRLINES]}
            onFilterChange={handleFilterChange}
            selectedItems={activeFilters[FlightFilterType.AIRLINES]}
          />
        </FilterChipDropdown>
      )}

      {filterItems[FlightFilterType.PRICES].length > 1 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.PRICES]}
          isActive={isPriceActive}
        >
          <FlightSingleFilterPrice
            items={filterItems[FlightFilterType.PRICES]}
            onFilterChange={handleFilterChange}
            selectedMinItem={[...activeFilters[FlightFilterType.MIN_PRICES]][0]}
            selectedMaxItem={[...activeFilters[FlightFilterType.MAX_PRICES]][0]}
            priceType={priceType}
            onPriceTypeChange={onPriceTypeChange}
          />
        </FilterChipDropdown>
      )}

      <FilterChipDropdown
        label={flightFiltersModalLabelMap[FlightFilterType.TIMES]}
        isActive={isTimeActive}
      >
        <FlightSingleFilterTime
          onFilterChange={handleFilterChange}
          selectedDepartureMinItem={[...activeFilters[FlightFilterType.MIN_DEPARTURE_TIME]][0]}
          selectedDepartureMaxItem={[...activeFilters[FlightFilterType.MAX_DEPARTURE_TIME]][0]}
          selectedArrivalMinItem={[...activeFilters[FlightFilterType.MIN_ARRIVAL_TIME]][0]}
          selectedArrivalMaxItem={[...activeFilters[FlightFilterType.MAX_ARRIVAL_TIME]][0]}
          departureAirportCode={originAirportCode}
          arrivalAirportCode={destinationAirportCode}
          departureTimeItems={filterItems[FlightFilterType.DEPARTURE_TIMES]}
          arrivalTimeItems={filterItems[FlightFilterType.ARRIVAL_TIMES]}
        />
      </FilterChipDropdown>

      {filterItems[FlightFilterType.DURATIONS].length > 1 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.DURATIONS]}
          isActive={!!filters.durations}
        >
          <FlightSingleFilterDuration
            items={filterItems[FlightFilterType.DURATIONS]}
            onFilterChange={handleFilterChange}
            selectedItem={[...activeFilters[FlightFilterType.DURATIONS]][0]}
          />
        </FilterChipDropdown>
      )}

      {activeFilterCount > 0 && (
        <TextButton kind="tertiary" size="medium" onClick={resetAllFilters}>
          {intl.formatMessage({
            id: 'faLgDM2T7ihJeAe-9Gzs',
            defaultMessage: 'Clear all',
            description: 'Action label to clear all applied flight filters in the trip planner drawer',
          })}
        </TextButton>
      )}
    </ChipsRow>
  )
}

export default TripFlightFiltersPanel
